import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { usePreferences } from "./hooks/usePreferences";
// 161B §1 — the one negative-zero normaliser, shared by all three currency
// formatters (this file's two and utils.ts's). utils.ts imports nothing from
// here, so this direction adds no cycle.
import { noNegZero, moneyText, moneyFigure } from "./utils"; // 207 §10 (i) — the documents' money face
import { FALLBACK_LANG, interpolate, resolve, type I18nCatalog } from "./lib/i18nResolve"; // 208 §20 — the resolver, one definition a recording runs

const I18nCtx = createContext<any>(null);
// Pre-auth fallback: if no user is logged in yet, the prefs cache is empty —
// fall back to a localStorage cache of the last chosen language so the login
// screen renders in the right locale.
const LS_KEY = "lx:lang";
const SUPPORTED_LANGS = ["en", "fr", "nl"];

/**
 * ── 82l — ASK THE BROWSER BEFORE FALLING BACK TO ENGLISH ────────────────────
 *
 * [MEASURED 2026-08-15] `navigator.language` appeared NOWHERE in `src/`. The
 * chain was `prefs.lang → lx:lang → "en"`, so a machine that had never signed
 * in here got English — **including every Belgian browser announcing `fr-BE` or
 * `nl-BE`, which is to say almost all of them.** The information was being sent
 * on every request and thrown away.
 *
 * `navigator.languages` is an ORDERED preference list, so it is walked in
 * order: a browser configured `nl-BE, fr-BE, en` wants Dutch, and taking the
 * first supported match is what honours that rather than whichever of ours
 * happens to appear.
 *
 * MATCHED ON THE PRIMARY SUBTAG ONLY. `fr-BE`, `fr-FR` and bare `fr` are all
 * French to this application — it ships three languages, not three locales, and
 * `LOCALE_TAGS` below already pins the region for `Intl` formatting separately.
 *
 * ⚠ AND IT IS DELIBERATELY NOT MIRRORED TO `lx:lang`. That key means "a person
 * CHOSE this", and it outranks the browser in the chain below; writing a guess
 * into it would make an inference indistinguishable from a decision, and the
 * language control on the login screen would come up looking as though it had
 * already been used. The browser is consulted afresh instead — it costs
 * nothing, and it stays correct when somebody reconfigures it.
 */
function browserLang(): string | null {
  try {
    if (typeof navigator === "undefined") return null;
    const tags: string[] = navigator.languages?.length
      ? [...navigator.languages]
      : navigator.language
        ? [navigator.language]
        : [];
    for (const tag of tags) {
      const primary = String(tag || "").toLowerCase().split("-")[0];
      if (SUPPORTED_LANGS.includes(primary)) return primary;
    }
  } catch {}
  return null;
}

// BCP-47 locale tags used for Intl.* date / number formatting.
// Maps the user-facing language code (en/fr/nl) to a region-qualified
// locale. Belgium-defaults for fr/nl since the firm is Brussels-based.
const LOCALE_BY_LANG: Record<string, any> = { en: "en-GB", fr: "fr-BE", nl: "nl-BE" };

export function localeFor(lang: any) {
  return LOCALE_BY_LANG[lang] || LOCALE_BY_LANG[FALLBACK_LANG];
}

// 208 §20 (the extraction) — `interpolate` and `resolve` live in src/lib/i18nResolve.ts, unchanged in what they do.

export function I18nProvider({ children }: any) {
  const [data, setData] = useState<I18nCatalog>({});
  const { prefs, setPref } = usePreferences();
  // The chain, most explicit first: what this person set on their account, then
  // what somebody chose on this machine, THEN what the browser asks for, and
  // only then English. 82l inserted the third step; the first two are
  // decisions and outrank it, English is what is left when nothing is known.
  // ── 82m — THE MACHINE'S CHOICE, AS STATE RATHER THAN A READ ───────────────
  //
  // `lx:lang` used to be read straight out of localStorage on every render,
  // which is fine while only `setLang` writes it — that call goes through
  // `setPref` and re-renders the tree anyway. The login screen's control cannot
  // use `setPref`: there is no account yet, so the PATCH to /me/preferences
  // 401s, warns on the console and toasts. So the value is held as state here,
  // seeded from the key, and `chooseLang` below moves both together.
  const [choice, setChoice] = useState<string | null>(() => {
    try {
      const ls = localStorage.getItem(LS_KEY);
      return ls && SUPPORTED_LANGS.includes(ls) ? ls : null;
    } catch { return null; }
  });

  // The chain, most explicit first: what this person set on their account, then
  // what somebody chose on this machine, THEN what the browser asks for, and
  // only then English. 82l inserted the third step; the first two are
  // decisions and outrank it, English is what is left when nothing is known.
  const lang = (() => {
    const p = prefs.lang;
    if (p && SUPPORTED_LANGS.includes(p)) return p;
    if (choice) return choice;
    const nav = browserLang();
    if (nav) return nav;
    return FALLBACK_LANG;
  })();

  const load = useCallback(async () => {
    try {
      const r = await fetch("/api/translations", { credentials: "same-origin" }); // 167 §2d
      if (!r.ok) return;
      setData(await r.json());
    } catch {}
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setLang = (l: any) => {
    setPref("lang", l);
    chooseLang(l);
  };

  /**
   * 82m — SET THE LANGUAGE ON THIS MACHINE, WITHOUT AN ACCOUNT.
   *
   * The same `lx:lang` key `setLang` mirrors to — deliberately not a second
   * one. `api.ts`'s logout sweep already spares it by name, so a choice made at
   * the door survives signing out, which is the whole point of making it there.
   *
   * What it does NOT do is touch `setPref`: that PATCHes /me/preferences, and
   * pre-auth there is nobody to PATCH. Once signed in, the account's own
   * preference outranks this in the chain above.
   */
  const chooseLang = (l: string) => {
    if (!SUPPORTED_LANGS.includes(l)) return;
    setChoice(l);
    try {
      localStorage.setItem(LS_KEY, l);
    } catch {}
  };

  // Stable ref to current lang/data so server-translated message helpers
  // (used outside React tree) can read the live value.
  if (typeof window !== "undefined") {
    window.__lxLang = lang;
    window.__lxI18n = { data, lang, t: (k: any, v: any) => resolve(data, lang, k, v) };
  }

  const t = useCallback(
    (key: any, vars: any) => resolve(data, lang, key, vars),
    [data, lang],
  );
  /**
   * ── BRIEF 105 §1b-ii — RESOLVE A KEY IN A LANGUAGE THAT IS NOT THE READER'S ─
   *
   * `t` is bound to `lang` and that is right for every screen: one reader, one
   * language. The enrolment invitation is the first thing in this app written
   * for somebody who is NOT the reader — 103 §1 and 96 §3b hand the link to an
   * administrator to pass on, `Collaborator` has no `lang` column, and 105
   * §1b-ii rules that none is being added. So the draft carries the
   * instructions in EVERY language the firm has, one after another, and that
   * needs a resolver taking the language as an argument.
   *
   * Same `resolve` as `t`, same English fallback, same `vars.fallback` — the
   * only difference is which catalog is asked first. Widened rather than
   * duplicated (rule 4: never approximate; if a primitive doesn't fit, widen
   * it), so a per-language render can never drift from a per-reader one.
   */
  const tIn = useCallback(
    (lng: string, key: string, vars?: Record<string, unknown>) =>
      resolve(data, lng, key, vars),
    [data],
  );
  const locale = useMemo(() => localeFor(lang), [lang]);
  const available = Object.keys(data).sort();

  // Locale-aware formatters — replace fr-BE-pinned helpers in utils.js for
  // call sites that have access to the i18n context.
  const fd = useCallback(
    (d: any) => {
      if (!d) return "—";
      try {
        return new Date(d).toLocaleDateString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      } catch {
        return "—";
      }
    },
    [locale],
  );

  // 169 R8 — `fc(n, currency)`: another currency prints with its ISO code.
  // 207 §10 (i) — the documents' face in every language: `moneyText` (utils.ts).
  const fc = useCallback(
    (n: any, currency?: string | null) => {
      if (n == null || n === "") return "—";
      try {
        return moneyText(noNegZero(+n), currency); // 161B §1 — never `-0,00 €`; see utils.ts
      } catch {
        return String(n);
      }
    },
    [],
  );

  // 207 §10 (i) — the documents' figure in every language (utils.ts `moneyFigure`).
  const fq = useCallback(
    (n: any) => {
      if (n == null || n === "") return "—";
      try {
        return moneyFigure(+n);
      } catch {
        return String(n);
      }
    },
    [],
  );

  // Minute-formatter: "1h 5min" / "45 min" — translates the unit suffixes.
  const fmin = useCallback(
    (m: any) => {
      if (!m) return t("time.zero_min", { defaultValue: "0 min" });
      const h = Math.floor(m / 60);
      const mn = m % 60;
      if (h > 0 && mn > 0) return t("time.h_min", { h, mn });
      if (h > 0) return t("time.h_only", { h });
      return t("time.min_only", { mn });
    },
    [t],
  );

  return (
    <I18nCtx.Provider
      value={{
        t,
        tIn,
        lang,
        locale,
        setLang,
        chooseLang,
        supported: SUPPORTED_LANGS,
        available,
        reload: load,
        fd,
        fc,
        fq,
        fmin,
      }}
    >
      {children}
    </I18nCtx.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(I18nCtx);
  if (!ctx) {
    // Out-of-tree consumers (e.g. OneDrivePicker, which is mounted via
    // ReactDOM.createRoot into a body-attached div) don't see the
    // I18nProvider context. Fall through to the imperative window.__lxI18n
    // bridge populated by the provider on every render. Honour vars.fallback
    // and pluralisation just like the in-tree resolver.
    const winI18n = typeof window !== "undefined" ? window.__lxI18n : null;
    const winLang =
      typeof window !== "undefined" && window.__lxLang
        ? window.__lxLang
        : FALLBACK_LANG;
    const lc = localeFor(winLang);
    const tFn = (k: any, vars: any) => {
      if (winI18n && winI18n.data)
        return resolve(winI18n.data, winI18n.lang || winLang, k, vars);
      // No bridge yet (pre-mount) — honour fallback if provided, else key.
      if (vars && typeof vars.fallback === "string")
        return interpolate(vars.fallback, vars);
      return interpolate(k, vars);
    };
    return {
      t: tFn,
      /* 105 §1b-ii — the same widening on the out-of-tree arm. A consumer
         mounted outside the provider (OneDrivePicker and its kin) must not get
         a context whose shape differs from the in-tree one: that is how a
         helper works on one surface and returns `undefined` on another. */
      tIn: (lng: string, k: string, vars?: Record<string, unknown>) => {
        if (winI18n && winI18n.data) return resolve(winI18n.data, lng, k, vars);
        if (vars && typeof vars.fallback === "string")
          return interpolate(vars.fallback, vars);
        return interpolate(k, vars);
      },
      lang: winLang,
      locale: lc,
      setLang: () => {},
      chooseLang: () => {},
      supported: SUPPORTED_LANGS,
      available: [FALLBACK_LANG],
      reload: () => {},
      fd: (d: any) =>
        d
          ? new Date(d).toLocaleDateString(lc, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "—",
      fc: (n: any, currency?: string | null) => {
        if (n == null) return "—";
        // 169 R8 — the document's currency; 207 §10 (i) — the documents' face
        // (utils.ts `moneyText`). 161B §1 — never `-0,00 €`.
        return moneyText(noNegZero(+n), currency);
      },
      fq: (n: any) => (n == null ? "—" : moneyFigure(+n)),
      fmin: (m: any) => {
        if (!m) return "0 min";
        const h = Math.floor(m / 60);
        const mn = m % 60;
        return h > 0 ? (mn > 0 ? `${h}h ${mn}min` : `${h}h`) : `${mn} min`;
      },
    };
  }
  return ctx;
}

// Imperative read for non-React code paths (e.g. helpers that compose
// server-error toasts before a t() handle is available).
export function tImperative(key: any, vars: any) {
  if (typeof window !== "undefined" && window.__lxI18n) {
    return window.__lxI18n.t(key, vars);
  }
  // 59 §3 — WITHOUT THE BRIDGE THIS USED TO RENDER THE KEY. Every `_tImp`
  // call site passes `{ fallback: "<the English>" }`, and the two resolvers
  // beside this one — `resolve()` above and `useTranslation`'s out-of-tree
  // arm — both honour it; only this one did not, so a call before the
  // provider's first render put `proforma.header.pro_forma` on the screen
  // instead of "PRO FORMA". Found by routing an EntityList column through
  // here in this slice: the byte-identity freeze rendered the key.
  if (vars && typeof vars.fallback === "string") return interpolate(vars.fallback, vars);
  return key;
}

export function langImperative() {
  if (typeof window !== "undefined" && window.__lxLang) return window.__lxLang;
  return FALLBACK_LANG;
}
